import { useMutation } from 'react-query';
import { gateModal } from '../../../design-system';
import { toIsoDate } from '../../../utils';
import { gateOutsService, removeCache } from '../../../services';
import { IdProps } from '../../../types';

export const useAddGateOutMutation = () => {
  return useMutation(
    (props: any = {}) => {
      const otherProps: any = {};
      if (props.releaseNumber) otherProps.releaseNumber = props.releaseNumber;
      if (props.comment) otherProps.comment = props.comment;

      props = {
        containers: props.containerNumber.map((number: any, i: any) => ({
          number,
          id: props.containerId[i],
        })),
        authorizedLicensePlateNumbers: props.licensePlateNumber,
        validFrom: toIsoDate(
          props.validFrom.set({
            hour: 1,
            minute: 0,
            second: 0,
            millisecond: 0,
          }),
        ),
        validTo: toIsoDate(
          props.validTo.set({
            hour: 23,
            minute: 0,
            second: 0,
            millisecond: 0,
          }),
        ),
        clientId: props.clientId,
        yardId: props.yardId,
        ...otherProps,
      };

      return gateOutsService.add(props).then((res) => res.data);
    },
    {
      onSuccess: () => {
        removeCache(['container', 'gate-out', 'sale', 'key']);
      },
    },
  );
};

export const useCreateGateOutsFilterMutation = () => {
  return useMutation(
    (props: any) => {
      const containerNumbers = props?.containers
        .map((v: any) => String(v).trim().toUpperCase())
        .filter(Boolean);

      const authorizedLicensePlateNumbers = props?.licensePlateNumber
        .map((v: any) => String(v).trim().toUpperCase())
        .filter(Boolean);

      const payload: any = {
        ...(containerNumbers.length ? { containerNumbers } : {}),
        ...(authorizedLicensePlateNumbers.length ? { authorizedLicensePlateNumbers } : {}),
        ...(props?.requestedCount > 0 ? { requestedCount: props.requestedCount } : {}),
        ...(props?.type ? { type: props.type } : {}),
        ...(props?.condition ? { condition: props.condition } : {}),
        validFrom: toIsoDate(
          props.validFrom.set({
            hour: 1,
            minute: 0,
            second: 0,
            millisecond: 0,
          }),
        ),
        validTo: toIsoDate(
          props.validTo.set({
            hour: 23,
            minute: 0,
            second: 0,
            millisecond: 0,
          }),
        ),
        clientId: props.clientId,
        yardId: props.yardId,
      };

      return gateOutsService.createFilter(payload);
    },
    {
      onSuccess: () => {
        removeCache(['gate-out', 'key']);
      },
    },
  );
};

export const useCreateGateOutsByFilterMutation = () => {
  return useMutation(
    (props: any) => {
      return gateOutsService.createByFilter(props);
    },
    {
      onSuccess: () => {
        removeCache(['container', 'gate-out', 'sale', 'key']);
      },
    },
  );
};

export const useFilterLoadingAllowedGateOutMutation = () => {
  return useMutation(
    (id: string) => {
      return gateOutsService.filterLoadingAllowed(id);
    },
    {
      onSuccess: () => {
        removeCache(['gate-out', 'key']);
      },
    },
  );
};

export const useLoadingAllowedGateOutMutation = () => {
  return useMutation(
    (props: IdProps[]) => {
      return gateOutsService.loadingAllowed(props);
    },
    {
      onSuccess: () => {
        removeCache(['container', 'gate-out', 'key']);
      },
    },
  );
};

export const useLoadedGateOutMutation = () => {
  return useMutation(
    (id: string) => {
      const props: any = { driver: { licensePlateNumber: gateModal.licensePlateNumber } };

      if (gateModal.driverFirstName) props.driver.firstName = gateModal.driverFirstName;
      if (gateModal.driverLastName) props.driver.lastName = gateModal.driverLastName;
      if (gateModal.phoneNumber) props.driver.phoneNumber = gateModal.phoneNumber;
      if (gateModal.comment) props.comment = gateModal.comment;

      return gateOutsService.loaded({ id, props });
    },
    {
      onSuccess: () => {
        removeCache(['container', 'gate-out', 'key']);
      },
      onSettled: () => {
        Object.keys(gateModal).forEach((key) => {
          if (key !== 'open') delete gateModal[key];
        });
      },
    },
  );
};

export const useCompleteGateOutMutation = () => {
  return useMutation(
    (id: string) => {
      const props: any = { driver: { licensePlateNumber: gateModal.licensePlateNumber } };

      if (gateModal.driverFirstName) props.driver.firstName = gateModal.driverFirstName;
      if (gateModal.driverLastName) props.driver.lastName = gateModal.driverLastName;
      if (gateModal.phoneNumber) props.driver.phoneNumber = gateModal.phoneNumber;
      if (gateModal.comment) props.comment = gateModal.comment;

      return gateOutsService.complete({ id, props });
    },
    {
      onSuccess: () => {
        removeCache(['container', 'gate-out', 'inventory', 'yard', 'key']);
      },
      onSettled: () => {
        Object.keys(gateModal).forEach((key) => {
          if (key !== 'open') delete gateModal[key];
        });
      },
    },
  );
};

export const useExtendDateGateOutMutation = () => {
  return useMutation(
    (props: any) => {
      props.validTo = toIsoDate(
        props.validTo.set({
          hour: 23,
          minute: 0,
          second: 0,
          millisecond: 0,
        }),
      );

      return gateOutsService.extendDate(props);
    },
    {
      onSuccess: () => {
        removeCache(['container', 'gate-out', 'key']);
      },
    },
  );
};

export const useDeleteGateOutMutation = () => {
  return useMutation(
    (props: IdProps[]) => {
      return gateOutsService.delete(props);
    },
    {
      onSuccess: () => {
        removeCache(['container', 'gate-out', 'key']);
      },
    },
  );
};

export const useDeleteGateOutsByFilterMutation = () => {
  return useMutation(
    (id: any) => {
      return gateOutsService.deleteByFilter(id);
    },
    {
      onSuccess: () => {
        removeCache(['gate-out', 'key']);
      },
    },
  );
};
